import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const SignaturePad = forwardRef(function SignaturePad(_, ref) {
  const canvasRef = useRef(null);
  const strokes = useRef([]);
  const active = useRef(null);
  const [count, setCount] = useState(0);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.scale(ratio, ratio);
    context.strokeStyle = '#111815';
    context.lineWidth = 3;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    strokes.current.forEach((stroke) => {
      if (stroke.length < 2) return;
      context.beginPath();
      context.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length - 1; i += 1) {
        const middleX = (stroke[i].x + stroke[i + 1].x) / 2;
        const middleY = (stroke[i].y + stroke[i + 1].y) / 2;
        context.quadraticCurveTo(stroke[i].x, stroke[i].y, middleX, middleY);
      }
      context.stroke();
    });
    context.restore();
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);
      draw();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useImperativeHandle(ref, () => ({
    empty: () => strokes.current.length === 0,
    toBlob: () => new Promise((resolve, reject) => {
      canvasRef.current.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Could not save the signature.')),
        'image/png'
      );
    })
  }));

  const point = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const move = (event) => {
    if (!active.current) return;
    const events = event.getCoalescedEvents?.() || [event];
    events.forEach((item) => active.current.push(point(item)));
    draw();
  };

  return (
    <section>
      <canvas
        ref={canvasRef}
        className="signature"
        aria-label="Client signature area"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          active.current = [point(event)];
          strokes.current.push(active.current);
          setCount(strokes.current.length);
        }}
        onPointerMove={move}
        onPointerUp={() => {
          active.current = null;
          draw();
        }}
        onPointerCancel={() => {
          active.current = null;
        }}
      />
      <div className="signature-actions">
        <button type="button" className="button ghost" disabled={!count} onClick={() => {
          strokes.current.pop();
          setCount(strokes.current.length);
          draw();
        }}>Undo</button>
        <button type="button" className="button ghost" disabled={!count} onClick={() => {
          strokes.current = [];
          setCount(0);
          draw();
        }}>Clear</button>
      </div>
    </section>
  );
});

export default SignaturePad;